import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Spin, Empty, Badge } from "antd";
import { SearchOutlined, ReloadOutlined, LeftOutlined } from "@ant-design/icons";
import { getCustomerList, getCustomerDetail, type CustomerDTO } from "@ifa/utils";
import { getSurnameStrokeCount, getStrokeGroupLabel, getAllStrokeGroups } from "../utils/strokeCount";
import { CustomerDetailView } from "./components/CustomerDetailView";
import { FnaView } from "./components/FnaView";
import { HistoryOrderView } from "./components/HistoryOrderView";
import { PolicyholderView } from "./components/PolicyholderView";
import { InsuredView } from "./components/InsuredView";
import { BeneficiaryView } from "./components/BeneficiaryView";
import styles from "./index.module.css";

interface StrokeGroup {
  label: string;
  range: [number, number];
}

type ViewMode = "detail" | "fna" | "profile" | "insured" | "beneficiary" | "history";

export function CustomersList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [allCustomers, setAllCustomers] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>();
  const [customerDetail, setCustomerDetail] = useState<CustomerDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("detail");
  const sidebarListRef = useRef<HTMLDivElement>(null);

  // 处理从其他页面返回时的状态恢复
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedCustomerId) {
      setSelectedCustomerId(Number(state.selectedCustomerId));
      if (state?.viewMode) {
        setViewMode(state.viewMode as ViewMode);
      }
      // 清除 state,避免重复触发
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // 获取所有客户数据
  const fetchAllCustomers = useCallback(
    async (isRefresh: boolean = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const res = await getCustomerList({
          pageNum: 1,
          pageSize: 99999,
          name: searchText || undefined,
        });

        const list = res?.content || [];
        setAllCustomers(list);
      } catch (error) {
        console.error("加载客户列表失败:", error);
      } finally {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [searchText]
  );

  useEffect(() => {
    fetchAllCustomers();
  }, [fetchAllCustomers]);

  // 获取客户详情
  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerDetail(null);
      return;
    }

    let active = true;
    setDetailLoading(true);

    getCustomerDetail(selectedCustomerId)
      .then((res) => {
        if (!active) return;
        setCustomerDetail(res);
      })
      .catch((err) => {
        console.error("获取客户详情失败:", err);
        if (active) {
          setCustomerDetail(null);
        }
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedCustomerId]);

  const strokeGroupCounts = useMemo(() => {
    const groups = getAllStrokeGroups();
    const counts: Record<string, number> = {};

    groups.forEach((group) => {
      counts[group.label] = 0;
    });

    allCustomers.forEach((customer) => {
      // chineseSurname 在根级别,使用类型断言访问
      const surname = (customer as any).chineseSurname || "";
      const strokeCount = getSurnameStrokeCount(surname);
      const groupLabel = getStrokeGroupLabel(strokeCount);

      // 只统计有效分组标签的客户
      if (groupLabel) {
        counts[groupLabel] = (counts[groupLabel] || 0) + 1;
      }
    });

    return counts;
  }, [allCustomers]);

  // 只显示有客户的笔画分组
  const availableStrokeGroups = useMemo(() => {
    return getAllStrokeGroups().filter((group) => {
      const count = strokeGroupCounts[group.label] || 0;
      return count > 0;
    });
  }, [strokeGroupCounts]);

  // 按分组整理客户
  const customersByGroup = useMemo(() => {
    const groups: Record<string, CustomerDTO[]> = {};

    allCustomers.forEach((customer) => {
      // chineseSurname 在根级别,使用类型断言访问
      const surname = (customer as any).chineseSurname || "";
      const strokeCount = getSurnameStrokeCount(surname);
      const groupLabel = getStrokeGroupLabel(strokeCount);

      // 跳过没有有效分组标签的客户(姓氏为空或笔画数无效)
      if (!groupLabel) return;

      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(customer);
    });

    return groups;
  }, [allCustomers]);

  const handleRefresh = () => {
    fetchAllCustomers(true);
  };

  const handleSelectCustomer = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setViewMode("detail"); // 选择客户时重置为详情视图
  };

  const handleNewOrder = (customerId?: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (customerId) {
      navigate("/orders/new", { state: { customerId } });
    } else {
      navigate("/orders/new");
    }
  };

  const handleEditCustomer = () => {
    if (selectedCustomerId) {
      navigate(`/customers/${selectedCustomerId}`);
    }
  };

  const handleNavigateToSection = (section: ViewMode) => {
    setViewMode(section);
  };

  const handleBackToDetail = () => {
    setViewMode("detail");
  };

  // 获取当前视图的标题
  const getViewTitle = () => {
    switch (viewMode) {
      case "fna":
        return "FNA";
      case "profile":
        return "客戶投保人";
      case "insured":
        return "受保人";
      case "beneficiary":
        return "受益人";
      case "history":
        return "歷史訂單";
      default:
        return customerDetail?.chineseName || "";
    }
  };

  return (
    <div className={styles.customersLayout}>
      <header className={styles.customersHeader}>
        {/* 左侧:客户标题,固定280px与侧边栏对齐 */}
        <div className={styles.headerLeft}>客戶</div>

        {/* 中间竖线分隔 */}
        <div className={styles.headerDivider}></div>

        {/* 右侧:返回按钮/客户名称 + 搜尋框 */}
        <div className={styles.headerRight}>
          {viewMode !== "detail" ? (
            <div className={styles.headerTitleWithBack}>
              <div className={styles.backButton} onClick={handleBackToDetail}>
                <LeftOutlined style={{ fontSize: "14px" }} />
              </div>
              <div className={styles.headerTitle}>{getViewTitle()}</div>
            </div>
          ) : (
            <>
              <div className={styles.headerTitle}>{getViewTitle()}</div>
              <Input
                placeholder="搜尋客戶名稱或證件號"
                prefix={<SearchOutlined />}
                style={{ width: 280 }}
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={() => fetchAllCustomers()}
                allowClear
              />
            </>
          )}
        </div>
      </header>

      <div className={styles.customersBody}>
        {/* 左侧: 笔画分组 + 客户列表 */}
        <aside className={styles.customersSidebar}>
          <div className={styles.sidebarHeader}>
            <span>按姓氏筆畫</span>
            <ReloadOutlined spin={refreshing} onClick={handleRefresh} style={{ cursor: "pointer", color: "#64748B" }} />
          </div>

          <div className={styles.strokeGroupList} ref={sidebarListRef}>
            {loading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Spin />
              </div>
            ) : (
              availableStrokeGroups.map((group) => {
                const customers = customersByGroup[group.label] || [];
                const count = customers.length;

                return (
                  <div key={group.label} className={styles.strokeGroupContainer}>
                    {/* 分组标题 */}
                    <div className={styles.strokeGroupItem}>
                      <span className={styles.groupLabel}>{group.label}</span>
                      <Badge
                        count={count}
                        overflowCount={999}
                        color="#F1F5F9"
                        styles={{
                          indicator: {
                            fontSize: "12px",
                            color: "#64748B",
                          },
                        }}
                      />
                    </div>

                    {/* 客户列表 */}
                    <div className={styles.customerList}>
                      {customers.map((customer) => {
                        const isSelected = selectedCustomerId === (customer.customerId || customer.id);
                        return (
                          <div
                            key={customer.customerId || customer.id}
                            className={`${styles.customerItem} ${isSelected ? styles.selected : ""} `}
                            onClick={() => handleSelectCustomer(customer.customerId || customer.id!)}
                          >
                            <div className={styles.customerName}>
                              {customer.chineseName || "-"} {customer.englishName || "-"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* 右侧: 客户详情 */}
        <main className={styles.customersMain}>
          {detailLoading ? (
            <div className={styles.loadingState}>
              <Spin size="large" />
            </div>
          ) : !customerDetail ? (
            <div className={styles.emptyState}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="請選擇客戶查看詳情" />
            </div>
          ) : (
            <>
              {viewMode === "detail" && <CustomerDetailView customer={customerDetail} onNewOrder={handleNewOrder} onEditCustomer={handleEditCustomer} onNavigateToSection={handleNavigateToSection} />}
              {viewMode === "fna" && <FnaView customer={customerDetail} onBack={handleBackToDetail} />}
              {viewMode === "profile" && <PolicyholderView customer={customerDetail} onBack={handleBackToDetail} />}
              {viewMode === "insured" && <InsuredView customer={customerDetail} onBack={handleBackToDetail} />}
              {viewMode === "beneficiary" && <BeneficiaryView customer={customerDetail} onBack={handleBackToDetail} />}
              {viewMode === "history" && <HistoryOrderView customer={customerDetail} onBack={handleBackToDetail} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
